import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';
import {
  EligiblePromotionsLabelsService,
  mergePromotionLabelsIntoProducts,
} from '@core/services/eligible-promotions-labels/eligible-promotions-labels.service';
import {
  applyProductLabelsVisibility,
  isPromotionLabel,
} from '@shared/utils/product-labels/product-labels.util';
import { DoodCategoryWithProduct } from '@shared/interfaces/category.interface';

/**
 * Local service for kiosk category page: loads category with products and applies
 * promotion-label visibility (only promotion-derived labels are visible on cards).
 */
@Injectable()
export class KioskCategoryPageService {
  constructor(
    private readonly shopRefiner: ShopStoreRefiner,
    private readonly eligiblePromotionsLabelsService: EligiblePromotionsLabelsService,
  ) {}

  /**
   * Returns category with products, with promotion labels merged in and visibility
   * set so that only promotion labels are visible on product cards.
   */
  getCategoryWithProducts$(categoryId: string): Observable<DoodCategoryWithProduct | null> {
    return combineLatest([
      this.shopRefiner.selectCategoryById(categoryId),
      this.eligiblePromotionsLabelsService.getPromotionLabelsByProductId$(),
    ]).pipe(
      map(([cat, promotionLabelsMap]) => {
        if (!cat) return null;
        const withPromoLabels = mergePromotionLabelsIntoProducts(
          cat.productsInformation ?? [],
          promotionLabelsMap,
        );
        return {
          ...cat,
          productsInformation: applyProductLabelsVisibility(withPromoLabels, isPromotionLabel),
        };
      }),
    );
  }
}
